import React, { useMemo } from "react";
import { Box, Skeleton, Typography, Stack } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LineChart } from "@mui/x-charts";
import SoothCard from "components/base/SoothCard";
import dayjs from "dayjs";
import { shiftHourFromUTC, displayHour } from "utils/time.util";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export default function EngagementByHourChart({ data, interval = 4 }) {
  const sortedLocalData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const localData = data.map((d) => ({
      ...d,
      localHour: shiftHourFromUTC(parseInt(d.xaxisLabel)),
    }));
    return localData.sort((a, b) => a.localHour - b.localHour);
  }, [data]);

  const seriesData = useMemo(() => {
    return sortedLocalData.map((d) => d.avgClicksPerUser);
  }, [sortedLocalData]);

  const xLabels = useMemo(() => {
    return sortedLocalData.map((d, index) => {
      const currentTime = displayHour(d.localHour);
      const nextHour =
        index < sortedLocalData.length - 1
          ? sortedLocalData[index + 1].localHour
          : (d.localHour + 1) % 24;
      const nextTime = displayHour(nextHour);

      return `${currentTime} - ${nextTime}`;
    });
  }, [sortedLocalData]);

  const tickValues = useMemo(() => {
    return xLabels.filter((_, idx) => idx % interval === 0);
  }, [xLabels, interval]);

  if (!data) {
    return <Skeleton variant="rectangular" width="100%" height={300} />;
  }

  return (
    <SoothCard
      sx={{
        backgroundColor: (theme) => theme.palette.secondary.main,
        borderRadius: 2,
        p: 2,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 2,
            justifyContent: "center",
          }}
        >
          <Typography variant="h4">Engagement</Typography>
          <AccessibleIconButton
            title="Hourly engagement patterns showing when your audience is most active and likely to interact with your content."
            size="small"
            sx={{ p: 0.5 }}
            tooltipProps={{
              placement: "top",
            }}
          >
            <InfoOutlinedIcon fontSize="small" color="primary" />
          </AccessibleIconButton>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flex: 1, textAlign: "right" }}
        >
          {dayjs.tz.guess()} timezone
        </Typography>
      </Stack>
      <Box sx={{ width: "100%", height: 300 }}>
        <LineChart
          series={[
            {
              data: seriesData,
              marker: { visible: true, shape: "circle", size: 6 },
              line: { tension: 0 },
            },
          ]}
          xAxis={[
            {
              scaleType: "point",
              data: xLabels,
              values: tickValues,
              labelRotation: 0,
            },
          ]}
          yAxis={[{ min: 0 }]}
          slotProps={{ legend: { hidden: true } }}
          sx={{ width: "100%", height: "100%" }}
        />
      </Box>
    </SoothCard>
  );
}
